import {
  type FC,
  useEffect,
  useRef
} from 'react'

import './_progressBar.scss'

interface Props {
  getVideoElement: () => HTMLVideoElement | null
  onSeek: (fraction: number) => void
  onScrubStart?: (fraction: number, left: number) => void
  onScrubMove?: (fraction: number, left: number) => void
  onScrubEnd?: (fraction: number, left: number) => void
}

const ProgressBar: FC<Props> = ({
  getVideoElement,
  onSeek,
  onScrubStart,
  onScrubMove,
  onScrubEnd
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  useEffect(() => {
    const video = getVideoElement()
    if (!video) {
      if (fillRef.current) {
        fillRef.current.style.transform = 'scaleX(0)'
      }
      return
    }

    let frameId: number | null = null
    let videoFrameId: number | null = null
    let running = false

    const setProgress = () => {
      if (draggingRef.current || !fillRef.current) return

      const duration = video.duration
      const progress =
        Number.isFinite(duration) && duration > 0
          ? Math.max(0, Math.min(1, video.currentTime / duration))
          : 0

      fillRef.current.style.transform = `scaleX(${progress})`
    }

    const runRaf = () => {
      if (!running) return

      setProgress()
      frameId = requestAnimationFrame(runRaf)
    }

    const runVideoFrame = () => {
      if (!running) return

      setProgress()

      videoFrameId = video.requestVideoFrameCallback(
        runVideoFrame
      )
    }

    const start = () => {
      if (running) return

      running = true
      setProgress()

      if ('requestVideoFrameCallback' in video) {
        videoFrameId = video.requestVideoFrameCallback(
          runVideoFrame
        )
      } else {
        frameId = requestAnimationFrame(runRaf)
      }
    }

    const stop = () => {
      running = false

      if (frameId !== null) {
        cancelAnimationFrame(frameId)
        frameId = null
      }

      if (
        videoFrameId !== null &&
        'cancelVideoFrameCallback' in video
      ) {
        video.cancelVideoFrameCallback(videoFrameId)
        videoFrameId = null
      }

      setProgress()
    }

    const reset = () => {
      if (!draggingRef.current && fillRef.current) {
        fillRef.current.style.transform = 'scaleX(0)'
      }
    }

    const sync = () => {
      setProgress()
    }

    video.addEventListener('play', start)
    video.addEventListener('playing', start)

    video.addEventListener('pause', stop)
    video.addEventListener('ended', stop)

    video.addEventListener('seeked', sync)
    video.addEventListener('loadedmetadata', sync)
    video.addEventListener('durationchange', sync)
    video.addEventListener('emptied', reset)

    if (!video.paused && !video.ended) {
      start()
    } else {
      sync()
    }

    return () => {
      stop()

      video.removeEventListener('play', start)
      video.removeEventListener('playing', start)
      video.removeEventListener('pause', stop)
      video.removeEventListener('ended', stop)
      video.removeEventListener('seeked', sync)
      video.removeEventListener('loadedmetadata', sync)
      video.removeEventListener('durationchange', sync)
      video.removeEventListener('emptied', reset)
    }
  }, [getVideoElement])

  const setVisualPosition = (fraction: number) => {
    if (!fillRef.current) return

    fillRef.current.style.transform =
      `scaleX(${Math.max(0, Math.min(1, fraction))})`
  }

  const getPosition = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const root = rootRef.current

    if (!root) {
      return {
        fraction: 0,
        left: 0
      }
    }

    const rect = root.getBoundingClientRect()

    const left = Math.max(
      0,
      Math.min(
        rect.width,
        event.clientX - rect.left
      )
    )

    return {
      fraction:
        rect.width > 0
          ? left / rect.width
          : 0,
      left
    }
  }

  const handlePointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    event.stopPropagation()
    event.preventDefault()

    draggingRef.current = true

    event.currentTarget.setPointerCapture(
      event.pointerId
    )

    const { fraction, left } =
      getPosition(event)

    setVisualPosition(fraction)

    onScrubStart?.(fraction, left)
    onScrubMove?.(fraction, left)

    onSeek(fraction)
  }

  const handlePointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const { fraction, left } =
      getPosition(event)

    onScrubMove?.(fraction, left)

    if (!draggingRef.current) return

    event.stopPropagation()
    event.preventDefault()

    setVisualPosition(fraction)
    onSeek(fraction)
  }

  const handlePointerUp = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (!draggingRef.current) return

    event.stopPropagation()
    event.preventDefault()

    const { fraction, left } =
      getPosition(event)

    draggingRef.current = false

    setVisualPosition(fraction)
    onSeek(fraction)
    onScrubEnd?.(fraction, left)

    if (
      event.currentTarget.hasPointerCapture(
        event.pointerId
      )
    ) {
      event.currentTarget.releasePointerCapture(
        event.pointerId
      )
    }
  }

  return (
    <div
      ref={rootRef}
      className='progressBar'
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onTouchStart={event => event.stopPropagation()}
      onTouchMove={event => event.stopPropagation()}
      onTouchEnd={event => event.stopPropagation()}
      onPointerCancel={handlePointerUp}
      onClick={event => {
        event.stopPropagation()
      }}
    >
      <div
        ref={fillRef}
        className='progressBar__range'
      />
    </div>
  )
}

export default ProgressBar